/**
 * 📡 Station Info Tab - Cetalingua FM
 * 
 * Form fields: name, location (lat/lon), city, country, description, species
 * Displays Station ID (500+) - assigned on registration
 * Shows preview card of how station appears on globe
 *
 * UPDATES:
 * ✅ FIXED: Writes to stations/{stationId} (not stations/{user.uid})
 * ✅ FIXED: Uses lat/lon field names (matches types.ts and GlobeScreen)
 * ✅ FIXED: Reads lat/lon from stationData (with fallback for legacy latitude/longitude)
 */

import React, { useState, useEffect } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase-config';
import { getSpeciesArray, getSpeciesName, SPECIES_DATA } from '../../../data/speciesData';
import './StationInfo.css';

const StationInfo = ({ user, stationData, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    latitude: '',
    longitude: '',
    city: '',
    country: '',
    species: [],
    isActive: true,
  });

  const allSpecies = getSpeciesArray();

  // ✅ FIXED: Read lat/lon with fallback for legacy latitude/longitude
  useEffect(() => {
    if (stationData) {
      setFormData({
        name: stationData.name || '',
        description: stationData.description || '',
        latitude: stationData.lat || stationData.latitude || '',
        longitude: stationData.lng || stationData.lon || stationData.longitude || '',
        city: stationData.city || '',
        country: stationData.country || '',
        species: stationData.species || [],
        isActive: stationData.isActive !== false,
      });
    }
  }, [stationData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSpeciesToggle = (speciesId) => {
    setFormData(prev => ({
      ...prev,
      species: prev.species.includes(speciesId)
        ? prev.species.filter(id => id !== speciesId)
        : [...prev.species, speciesId]
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('Station name is required');
      return;
    }

    setSaving(true);
    try {
      // ✅ FIXED: Write to stations/{stationId} instead of stations/{user.uid}
      const stationId = String(stationData?.stationId);
      if (!stationId || stationId === 'undefined') {
        alert('Station ID not found. Please contact support.');
        setSaving(false);
        return;
      }

      const stationRef = doc(db, 'stations', stationId);

      // ✅ FIXED: Destructure to separate lat/lon from form field names
      const { latitude, longitude, ...rest } = formData;

      await setDoc(stationRef, {
        ...rest,
        lat: parseFloat(latitude) || 0,
        lng: parseFloat(longitude) || 0,
        updatedAt: new Date().toISOString(),
      }, { merge: true });

      setIsEditing(false);
      if (onUpdate) onUpdate();
      alert('Station information saved!');
    } catch (error) {
      console.error('Error saving station:', error);
      alert('Failed to save: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'manatee': return '🦭';
      case 'whale': return '🐋';
      case 'dolphin': return '🐬';
      case 'porpoise': return '🐬';
      default: return '❓';
    }
  };

  return (
    <div className="station-info-tab">
      {/* Form Section */}
      <div className="info-form-section">
        <div className="section-header">
          <h2>📡 Station Information</h2>
          {!isEditing ? (
            <button className="edit-btn" onClick={() => setIsEditing(true)}>
              ✏️ Edit
            </button>
          ) : (
            <div className="edit-actions">
              <button className="cancel-btn" onClick={() => setIsEditing(false)}>
                Cancel
              </button>
              <button className="save-btn" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : '💾 Save'}
              </button>
            </div>
          )}
        </div>

        {/* Station ID Display */}
        <div className="station-id-box">
          <span className="id-label">Station ID:</span>
          <span className="id-value">{stationData?.stationId || 'Pending'}</span>
          <span className="id-note">This ID is used in all uploaded filenames</span>
        </div>

        {/* Form Fields */}
        <div className="form-grid">
          <div className="form-group full-width">
            <label>Station Name *</label>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Monterey Bay Hydrophone"
              />
            ) : (
              <p className="display-value">{formData.name || 'Not set'}</p>
            )}
          </div>

          <div className="form-group full-width">
            <label>Description</label>
            {isEditing ? (
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe your station and what listeners might hear..."
                rows={4}
              />
            ) : (
              <p className="display-value">{formData.description || 'No description'}</p>
            )}
          </div>

          <div className="form-group">
            <label>Latitude *</label>
            {isEditing ? (
              <input
                type="number"
                name="latitude"
                value={formData.latitude}
                onChange={handleChange}
                placeholder="e.g., 36.6002"
                step="any"
              />
            ) : (
              <p className="display-value">{formData.latitude || '0'}</p>
            )}
          </div>

          <div className="form-group">
            <label>Longitude *</label>
            {isEditing ? (
              <input
                type="number"
                name="longitude"
                value={formData.longitude}
                onChange={handleChange}
                placeholder="e.g., -121.8947"
                step="any"
              />
            ) : (
              <p className="display-value">{formData.longitude || '0'}</p>
            )}
          </div>

          <div className="form-group">
            <label>City</label>
            {isEditing ? (
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="e.g., Monterey"
              />
            ) : (
              <p className="display-value">{formData.city || 'Not set'}</p>
            )}
          </div>

          <div className="form-group">
            <label>Country</label>
            {isEditing ? (
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="e.g., USA"
              />
            ) : (
              <p className="display-value">{formData.country || 'Not set'}</p>
            )}
          </div>

          <div className="form-group full-width">
            {isEditing ? (
              <label className="checkbox-wrapper">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                <span>Station is active and visible on map</span>
              </label>
            ) : (
              <div className="status-display">
                <span className={`status-badge ${formData.isActive ? 'active' : 'inactive'}`}>
                  {formData.isActive ? '🟢 Active' : '🔴 Inactive'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Species Section */}
        <div className="species-section">
          <h3>🐋 Species Present at Station</h3>
          <p className="species-help">Select species that may be heard at your location</p>

          {isEditing ? (
            <div className="species-grid">
              {['manatee', 'whale', 'dolphin', 'porpoise'].map(category => (
                <div key={category} className="species-category">
                  <h4>{getCategoryIcon(category)} {category.charAt(0).toUpperCase() + category.slice(1)}s</h4>
                  <div className="species-list">
                    {allSpecies.filter(s => s.category === category).map(species => (
                      <label key={species.id} className="species-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.species.includes(species.id)}
                          onChange={() => handleSpeciesToggle(species.id)}
                        />
                        <span>{species.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="species-tags">
              {formData.species.length > 0 ? (
                formData.species.map(id => (
                  <span key={id} className="species-tag">
                    {getSpeciesName(id)}
                  </span>
                ))
              ) : (
                <p className="no-species">No species selected</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Preview Section */}
      <div className="preview-section">
        <h2>📱 App Preview</h2>
        <p className="preview-subtitle">How listeners see your station</p>

        <div className="station-preview-card">
          <div className="preview-header">
            <div className="preview-icon">📡</div>
            <div className="preview-info">
              <h3>{formData.name || 'Station Name'}</h3>
              <p className="preview-location">
                {formData.city && formData.country 
                  ? `${formData.city}, ${formData.country}` 
                  : 'Location not set'}
              </p>
            </div>
            <div className={`preview-status ${formData.isActive ? 'live' : 'offline'}`}>
              {formData.isActive ? 'LIVE' : 'OFFLINE'}
            </div>
          </div>

          <p className="preview-description">
            {formData.description || 'No description provided.'}
          </p>

          <div className="preview-species-section">
            <h4>Species You May Hear:</h4>
            <div className="preview-species-list">
              {formData.species.slice(0, 5).map(id => {
                const species = SPECIES_DATA[id];
                return (
                  <span key={id} className="preview-species-tag">
                    {getCategoryIcon(species?.category)} {getSpeciesName(id)}
                  </span>
                );
              })}
              {formData.species.length > 5 && (
                <span className="preview-more">+{formData.species.length - 5} more</span>
              )}
              {formData.species.length === 0 && (
                <span className="preview-none">None listed</span>
              )}
            </div>
          </div>

          <div className="preview-coords">
            📍 {formData.latitude || '0'}, {formData.longitude || '0'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StationInfo;
